import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { RatingBar, RATING_KEYS, RATING_LABELS, Stars } from "../components/Ratings";
import { ReviewCard } from "../components/ReviewCard";
import { Footer } from "../components/Footer";
import { RENTING_TIPS } from "../lib/content";
import { propPhoto, FALLBACK } from "./Home";
import { ShieldCheck, PenLine, AlertTriangle, Lightbulb } from "lucide-react";

export default function PropertyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/properties/${slug}`).then((r) => setData(r.data)).catch(() => setError("Property not found"));
  }, [slug]);

  useEffect(() => {
    if (data) document.title = `${data.full_address} — tenant reviews | Tenverly`;
  }, [data]);

  if (error) return (<div className="bg-[#F4F7FA] min-h-screen"><Navbar /><div className="max-w-4xl mx-auto px-5 py-24 text-center text-slate-500" data-testid="property-not-found">{error}</div></div>);
  if (!data) return (<div className="bg-[#F4F7FA] min-h-screen"><Navbar /><div className="max-w-4xl mx-auto px-5 py-24 text-center text-slate-400">Loading…</div></div>);

  const agg = data.aggregates;

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />

      {/* Insights header (navy) */}
      <section className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-5 py-10">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] font-mono text-mint">
              Property Insights: {data.full_address}
            </span>
            <button data-testid="property-write-review-btn" onClick={() => navigate(`/write-review?slug=${slug}`)}
              className="flex items-center gap-2 bg-mint text-navy font-semibold px-5 py-2.5 rounded-xl hover:bg-mint-light transition-colors">
              <PenLine className="w-4 h-4" /> Write a review
            </button>
          </div>

          <div className="mt-6">
            {/* Property card */}
            <div className="bg-white text-navy rounded-2xl p-5 flex gap-5 max-w-2xl" data-testid="property-header">
              <img src={propPhoto(data)} alt={data.address_line} onError={(e) => { e.currentTarget.src = FALLBACK; }} className="w-32 h-32 object-cover rounded-xl shrink-0" />
              <div className="flex-1">
                <h1 className="font-heading font-extrabold text-2xl leading-tight">{data.address_line}</h1>
                <p className="text-slate-500">{data.city}, {data.postcode}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-lg font-mono font-bold">
                    {agg.count ? agg.overall.toFixed(1) : "—"} <Stars value={agg.overall} />
                  </span>
                  {data.common_issue && <span className="text-sm text-slate-500">{data.common_issue}</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.subscription_active && (
                    <span className="inline-flex items-center gap-1.5 bg-navy text-white px-3 py-1 rounded-lg text-sm font-semibold font-mono" data-testid="verified-landlord-badge">
                      <ShieldCheck className="w-4 h-4 text-mint" /> Verified Responsible Landlord
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category breakdown */}
      <div className="max-w-7xl mx-auto px-5 py-10">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 shadow-sm">
          {RATING_KEYS.map((k) => <RatingBar key={k} label={RATING_LABELS[k]} value={agg.ratings[k]} />)}
        </div>

        <h2 className="mt-12 font-heading font-extrabold text-2xl text-navy">Tenant Reviews</h2>
        <div className="mt-3 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900" data-testid="reviews-disclaimer">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <p>These are the personal views and experiences of tenants who have verified they held a tenancy at this property. The individual claims and opinions within each review have <strong>not been independently fact-checked or verified</strong> by Tenverly. Reviews reflect the opinion of their author only and do not represent the views of Tenverly. If you believe a review is inaccurate, landlords can respond or raise a formal dispute.</p>
        </div>
        <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="reviews-list">
          {data.reviews.length === 0 ? (
            <div className="col-span-2 bg-white border border-dashed border-slate-300 rounded-2xl p-8" data-testid="no-reviews">
              <div className="text-center">
                <h3 className="font-heading font-bold text-xl text-navy">No reviews yet for this property.</h3>
                <p className="mt-2 text-slate-500">Live here, or used to? Be the first to leave a verified review and help future tenants.</p>
                <button data-testid="empty-leave-review" onClick={() => navigate(`/write-review?slug=${slug}`)}
                  className="mt-4 inline-flex items-center gap-2 bg-mint text-white font-semibold px-6 py-3 rounded-xl hover:bg-mint-dark transition-colors">
                  <PenLine className="w-4 h-4" /> Live here? Leave a review
                </button>
              </div>
              <div className="mt-8 pt-6 border-t border-slate-100">
                <p className="flex items-center gap-2 font-semibold text-navy"><Lightbulb className="w-5 h-5 text-mint-dark" /> Thinking of renting here? A few tips first:</p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-600">
                  {RENTING_TIPS.slice(0, 4).map((tip, i) => (
                    <li key={i} className="flex gap-2"><span className="text-mint-dark font-bold">·</span> {tip.t}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : data.reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
        </div>
      </div>
      <Footer />
    </div>
  );
}
